import React, {useMemo} from 'react';
import clsx from 'clsx';
import {Typography} from '@mui/material';
import {AssetImage} from '@netx/core-asset-next/components/AssetImage';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {bytesToString} from '@netx/core-next/utils/string';
import {IOrganizeAssetsWarningObjectItemAsset} from '../../schema';
import {
	CATEGORY_COLLISION_ITEM_NAME,
	categoryCollisionItemClasses,
	type CategoryCollisionItemClasses,
	getCategoryCollisionItemUtilityClass,
} from './categoryCollisionItemClasses';

type CategoryCollisionItemOwnerState =
	WithClasses<CategoryCollisionItemClasses>;

const useUtilityClasses = (ownerState: CategoryCollisionItemOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getCategoryCollisionItemUtilityClass, classes);
};

const CategoryCollisionItemRoot = styled('div', {
	name: CATEGORY_COLLISION_ITEM_NAME,
	slot: 'Root',
	// overridesResolver: (props, styles) => [
	//     styles.root,
	// ],
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	'& + &': {
		marginTop: theme.spacing(3),
	},
}));

const CategoryCollisionItemThumb = styled('div', {
	name: CATEGORY_COLLISION_ITEM_NAME,
	slot: 'Thumb',
})(({theme}) => ({
	display: 'flex',
	flexShrink: 0,
	width: 100,
	height: 100,
	overflow: 'hidden',
	position: 'relative',
	borderRadius: theme.shape.borderRadius,
	// boxShadow: theme.shadows[2],
	border: `1px solid ${theme.palette.divider}`,
	background: theme.palette.custom.background.light,
}));

const CategoryCollisionItemDetails = styled('div', {
	name: CATEGORY_COLLISION_ITEM_NAME,
	slot: 'Details',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(0, 3),
}));

export interface CategoryCollisionItemProps
	extends
		React.HTMLAttributes<HTMLDivElement>,
		WithClasses<CategoryCollisionItemClasses> {
	item: IOrganizeAssetsWarningObjectItemAsset;
	type: 'incoming' | 'conflicting';
}

export const CategoryCollisionItem = ({
	classes: _classes,
	className,
	item,
	type,
}: CategoryCollisionItemProps) => {
	const ownerState: CategoryCollisionItemOwnerState = {
		classes: _classes,
	};

	const classes = useUtilityClasses(ownerState);

	const asset = useMemo(
		() => ({
			assetId: item.assetId,
			thumbUrl: item.thumbUrl,
			name: item.name,
			viewNames: [],
			fileheight: 150,
			filewidth: 150,
			previewHeight: 150,
			previewWidth: 150,
			previewUrl: '',
			zoomUrl: '',
			status: 1,
			fileFormatFamily: [],
			filetypelabel: '',
			file: '',
			jwt: null,
			moddate: '',
		}),
		[item],
	);

	return (
		<CategoryCollisionItemRoot className={clsx(classes.root, className)}>
			<CategoryCollisionItemThumb>
				<AssetImage asset={asset} disableXHR />
			</CategoryCollisionItemThumb>
			<CategoryCollisionItemDetails>
				<Typography variant="body2" fontWeight={700}>
					{type === 'conflicting'
						? i18n.t('i18n.existingAsset')
						: i18n.t('i18n.newAsset')}
				</Typography>
				<Typography variant="body2">{bytesToString(item.fileSize)}</Typography>
				<Typography variant="body2">{item.creationDate}</Typography>
			</CategoryCollisionItemDetails>
		</CategoryCollisionItemRoot>
	);
};
